import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { getKnowledgeOverview, type KnowledgeOverview } from "../api/knowledge.ts";

export function KnowledgeOverviewPage() {
  const { token } = useAuth();
  const [data, setData] = useState<KnowledgeOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    getKnowledgeOverview(token)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [token]);

  const cards = [
    { label: "Total Documents", count: data?.documents.total ?? 0, to: "/knowledge/documents", color: "border-blue-500/30 bg-blue-900/10" },
    { label: "Ready", count: data?.documents.ready ?? 0, to: "/knowledge/documents?status=READY", color: "border-green-500/30 bg-green-900/10" },
    { label: "Processing", count: data?.documents.processing ?? 0, to: "/knowledge/documents?status=PROCESSING", color: "border-yellow-500/30 bg-yellow-900/10" },
    { label: "Pending Indexing", count: data?.documents.pending ?? 0, to: "/knowledge/documents?status=PENDING", color: "border-teal-500/30 bg-teal-900/10" },
    { label: "Failed", count: data?.documents.failed ?? 0, to: "/knowledge/documents?status=FAILED", color: "border-red-500/30 bg-red-900/10" },
    { label: "Total Searches", count: data?.searches ?? 0, to: "/knowledge/search", color: "border-purple-500/30 bg-purple-900/10" },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Knowledge Overview</h1>
          <Link to="/knowledge/documents" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors">
            + Upload Document
          </Link>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {cards.map((card) => (
            <Link
              key={card.label}
              to={card.to}
              className={`block rounded-lg border p-5 transition-all hover:brightness-110 ${card.color}`}
            >
              <p className="text-3xl font-bold text-white">{loading ? "—" : card.count.toLocaleString()}</p>
              <p className="text-sm text-gray-400 mt-1">{card.label}</p>
            </Link>
          ))}
        </div>

        <div className="rounded-lg border border-blue-800/30 bg-blue-900/10 p-4">
          <p className="text-sm text-blue-300">
            <strong>Vector Index:</strong>{" "}
            {(data?.documents.ready ?? 0) > 0
              ? `${data!.documents.ready} documents indexed and searchable`
              : "No documents indexed yet. Upload documents to enable semantic search."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
          <Link to="/knowledge/search" className="block rounded-lg border border-gray-800 p-5 hover:border-gray-700 transition-colors">
            <h2 className="text-white font-semibold mb-1">Semantic Search</h2>
            <p className="text-sm text-gray-400">Search across all indexed documents using natural language queries.</p>
          </Link>
          <Link to="/knowledge/documents" className="block rounded-lg border border-gray-800 p-5 hover:border-gray-700 transition-colors">
            <h2 className="text-white font-semibold mb-1">Document Library</h2>
            <p className="text-sm text-gray-400">Browse, upload, and manage knowledge documents across all scopes.</p>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
